import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';

import { getEvent, getId as getEventId } from '../state/event';
import { checkPortal } from '../state/system';
import {
  queryGetConfig,
  useConfigKeysQuery,
  useConfigQuery,
  useConfigUpdateMutation,
} from '../services/config/queries';
import { ConfigurationEntry, ConfigurationKey } from '../services/config/types';
import { isFFEnabled } from '../state/flags';
import { oldUpdate } from '../services/config/api';
import { useQueryClient } from '@tanstack/react-query';

// VALID_NULLABLE_CONFIG_KEYS is the list of configuration keys that can have a null
// as valid value.
// These configurations should not be considered as configs with missing values and
// be replaced with default value if the value is null.
//
// One good example is `event_max_duration_days` which will have a null value if the
// event does not have a max duration set; otherwise it will have a number value.
const VALID_NULLABLE_CONFIG_KEYS = ['event_max_duration_days', 'reg_open_period'];

const useConfig = () => {
  const eventId = useSelector(getEventId);
  const { timezone } = useSelector(getEvent);
  const { isExperiencePortal } = useSelector(checkPortal);
  const isNewConfigUapiEnabled: boolean = useSelector(state =>
    isFFEnabled(state, 'new_config_uapi'),
  );
  const queryClient = useQueryClient();

  const keysQuery = useConfigKeysQuery([isNewConfigUapiEnabled], {
    enabled: isExperiencePortal,
  });
  const configQuery = useConfigQuery([eventId, isNewConfigUapiEnabled], { enabled: !!eventId });
  const { isLoading: isUpdatingConfig, mutateAsync: updateConfig } = useConfigUpdateMutation();
  // react-query return isLoading as true when the enabled flag is false
  // so in the case of CompanyPortal the keysQuery.isLoading will always return true
  // hence added one more check - !isExperiencePortal
  const loading =
    (keysQuery.isLoading && isExperiencePortal) || (configQuery.isLoading && !!eventId);
  const keys = keysQuery.data;
  const conf = configQuery.data;

  const merged: Record<string, { config: ConfigurationEntry; key: ConfigurationKey }> =
    useMemo(() => {
      const obj = (conf ?? []).reduce((acc, currentConfig) => {
        acc[currentConfig.key] = {
          config: currentConfig,
          key: keys?.find(({ key }) => key === currentConfig.key),
        };
        return acc;
      }, {});

      return Object.freeze(obj);
    }, [conf, keys]);

  // gets the value of a record from the configuration array stored in the state.
  // in case of key not being existent in the configuration object, it looks up
  // in the keys store and returns the default value (returns the first one when
  // there are multiple default values), if it doesn't exist in the default keys
  // array either then it return null
  const get = useCallback(
    (key: string) => {
      const target = merged[key];
      if (!target?.config) {
        return null;
      }

      const { config: targetConfig, key: targetKey } = target;

      // some config keys may have null as valid value; if this key is among those
      // and the value is null, return null - and do not use the default value
      if (VALID_NULLABLE_CONFIG_KEYS.includes(key) && targetConfig?.value === null) {
        return null;
      }

      if (targetKey?.type === 'datetime_range' && targetConfig?.value) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { start_datetime, end_datetime } = JSON.parse(targetConfig.value);
        return [moment.tz(start_datetime, timezone), moment.tz(end_datetime, timezone)];
      }

      if (targetKey?.type === 'boolean') {
        return targetConfig.value === '1';
      }

      // if value property exists in the target returns that, other wise look for the
      // default property, if that exists then splits the value of default using
      // `,` character so that it will return the first value in case there are multiple
      // default values; in rare scenario of neither value nor default property being
      // defined, return null
      const confValue = targetConfig?.value;
      const keyValue = targetKey?.default?.split(',')[0];

      if (confValue !== undefined) {
        return confValue;
      }

      if (keyValue !== undefined) {
        return keyValue;
      }

      return null;
    },
    [merged, timezone],
  );

  const find = useCallback((key: string) => merged[key]?.config, [merged]);

  // asynchronous function to either update a configuration object or create a new one
  // if it does not exist; returns the newly created/update record
  const set = useCallback(
    async (
      configs: { key: string; value: string | number; expiresAt?: string | null }[],
      // remove shouldUseNewUpdateConfig when ff new_config_uapi is removed
      shouldUseNewUpdateConfig = false,
    ) => {
      if (!shouldUseNewUpdateConfig) {
        configs.forEach(config => {
          const target = merged[config.key]?.config;

          if (typeof target === 'undefined') {
            throw new Error(`invalid configuration key ${config.key}`);
          }
        });
      }

      if (isNewConfigUapiEnabled || shouldUseNewUpdateConfig) {
        await updateConfig({ configs });

        return;
      }
      const response = configs.map(({ key, value }) => {
        const target = merged[key]?.config;

        return oldUpdate(eventId, target.id, { ...target, value });
      });

      await Promise.all(response);
      queryClient.invalidateQueries({
        queryKey: queryGetConfig.getQueryKey(eventId, isNewConfigUapiEnabled),
      });
    },
    [isNewConfigUapiEnabled, merged, updateConfig, eventId, queryClient],
  );

  /**
   * @description checks if the provided configuration keys are enabled
   * @param {String} getKeys - configuration key to check
   * @returns {boolean} true if and only if all the config keys passed are enabled.
   * return `true` if it exists AND its value is set to `1`
   */
  const isConfigEnabled = useCallback((key: string) => !!get(key), [get]);

  return {
    get,
    find,
    set,
    isConfigEnabled,
    // TODO: EXP-21528 - remove loading and use isUpdating and isFetching separately
    loading,
    keys,
    configurations: conf,
    isUpdating: isUpdatingConfig,
    isFetching: (keysQuery.isLoading && isExperiencePortal) || configQuery.isLoading,
  };
};

export default useConfig;
